'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';

/**
 * Minimum pixel height to treat as a keyboard. Real mobile keyboards are
 * 250px+. Values below this threshold come from the address bar
 * collapsing/expanding (~44px on iOS) or the gesture navigation bar (~48px
 * on Android) and must be ignored.
 */
const KEYBOARD_MIN_HEIGHT = 100;

/**
 * Tracks the mobile virtual keyboard height and exposes it as the
 * `--keyboard-offset` CSS custom property on `<html>`.
 *
 * When the keyboard is closed the value is `0px`. When open it equals the
 * pixel height of the keyboard. Bottom sheets use this to shift upward and
 * sit on top of the keyboard instead of being covered by it.
 *
 * How it works: `window.visualViewport.height` shrinks when the keyboard
 * opens. The difference between the initial viewport height (captured once
 * on mount) and the current visual viewport height is the keyboard height.
 * We capture `innerHeight` once to avoid false positives from the iOS
 * address bar collapsing (which changes `innerHeight` live) and Android
 * `innerHeight` lagging behind the visual viewport during keyboard
 * animation.
 *
 * Mount once at the app root via `<ViewportProvider />`. No-op on desktop
 * or when `visualViewport` is not supported.
 */
export function useVisualViewport() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
}
function _temp() {
  const vv = window.visualViewport;
  if (!vv) {
    return;
  }
  const viewport = vv;
  const initialHeight = window.innerHeight;
  let rafId = null;
  const sync = function sync() {
    rafId = null;
    const offset = initialHeight - viewport.height - viewport.offsetTop;
    const value = offset >= KEYBOARD_MIN_HEIGHT ? Math.round(offset) : 0;
    document.documentElement.style.setProperty("--keyboard-offset", `${value}px`);
  };
  const scheduleSync = function scheduleSync() {
    if (rafId !== null) {
      return;
    }
    rafId = requestAnimationFrame(sync);
  };
  scheduleSync();
  viewport.addEventListener("resize", scheduleSync);
  viewport.addEventListener("scroll", scheduleSync);
  return () => {
    viewport.removeEventListener("resize", scheduleSync);
    viewport.removeEventListener("scroll", scheduleSync);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    document.documentElement.style.removeProperty("--keyboard-offset");
  };
}